import ExcelJS from 'exceljs'

/** One parsed row, keyed by the sheet's header cell text (like the old `xlsx` `sheet_to_json`). */
export type SheetRow = Record<string, string | number | boolean | Date | null>

/**
 * Reads the first worksheet of an `.xlsx` file (or a `.csv`) into header-keyed row objects,
 * mirroring the output shape the old `xlsx` `sheet_to_json` produced: the first row is the
 * header, subsequent rows become objects keyed by header text, fully-empty rows are skipped,
 * and empty cells simply have no key (callers already default with `?? ''`).
 *
 * Legacy `.xls` (binary Excel 97-2003) is intentionally unsupported — `exceljs` cannot read it.
 * Callers restrict their file picker to `.xlsx`/`.csv`; an `.xls` still passed in throws.
 */
export async function readSheetAsRows(file: File): Promise<SheetRow[]> {
  const name = file.name.toLowerCase()

  if (name.endsWith('.csv')) {
    return parseCsv(await file.text())
  }
  if (name.endsWith('.xls')) {
    throw new Error('Legacy .xls files are not supported — please save as .xlsx or .csv.')
  }

  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(await file.arrayBuffer())
  const worksheet = workbook.worksheets[0]
  if (!worksheet) return []
  return worksheetToRows(worksheet)
}

/** Unwraps an exceljs cell value (formula/richtext/hyperlink objects) to a plain primitive. */
function cellValue(cell: ExcelJS.Cell): SheetRow[string] {
  const v = cell.value
  if (v === null || v === undefined) return null
  if (v instanceof Date) return v
  if (typeof v === 'object') {
    if ('result' in v) return (v.result as SheetRow[string]) ?? null // formula → its computed result
    if ('richText' in v) return v.richText.map(rt => rt.text).join('')
    if ('text' in v) return v.text ?? null // hyperlink → its display text
    return null // error cells and anything else → treat as empty
  }
  return v as string | number | boolean
}

function worksheetToRows(worksheet: ExcelJS.Worksheet): SheetRow[] {
  // Column number → trimmed header text, taken from the first row.
  const headers = new Map<number, string>()
  worksheet.getRow(1).eachCell({ includeEmpty: false }, (cell, col) => {
    const header = String(cellValue(cell) ?? '').trim()
    if (header) headers.set(col, header)
  })

  const rows: SheetRow[] = []
  for (let r = 2; r <= worksheet.rowCount; r++) {
    const sheetRow = worksheet.getRow(r)
    const obj: SheetRow = {}
    let hasValue = false
    for (const [col, header] of headers) {
      const value = cellValue(sheetRow.getCell(col))
      if (value === null || value === undefined || value === '') continue
      obj[header] = value
      hasValue = true
    }
    if (hasValue) rows.push(obj) // skip fully-empty rows (old `blankrows: false`)
  }
  return rows
}

/** Minimal RFC-4180-style CSV parser: handles quoted fields, embedded commas/quotes/newlines. */
function parseCsv(text: string): SheetRow[] {
  const table: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++ } // escaped quote
        else inQuotes = false
      } else {
        field += c
      }
      continue
    }
    if (c === '"') { inQuotes = true }
    else if (c === ',') { row.push(field); field = '' }
    else if (c === '\r') { /* ignore, handled by \n */ }
    else if (c === '\n') { row.push(field); table.push(row); row = []; field = '' }
    else { field += c }
  }
  // Flush the trailing field/row if the file didn't end with a newline.
  if (field.length > 0 || row.length > 0) { row.push(field); table.push(row) }

  if (table.length === 0) return []
  const headers = table[0].map(h => h.trim())

  const rows: SheetRow[] = []
  for (let r = 1; r < table.length; r++) {
    const cells = table[r]
    const obj: SheetRow = {}
    let hasValue = false
    headers.forEach((header, col) => {
      if (!header) return
      const value = (cells[col] ?? '').trim()
      if (value === '') return
      obj[header] = value
      hasValue = true
    })
    if (hasValue) rows.push(obj)
  }
  return rows
}
